import { BaseUserService } from './baseService';
import {
  PanelAccess,
  PanelSection,
  RoleDefaultAccess,
  UserIpHistory,
} from './types';

export class UserAccessService extends BaseUserService {
  /**
   * Récupère les accès aux pages du panel d'un utilisateur
   */
  async getUserPanelAccess(userId: number): Promise<{
    success: boolean;
    data: PanelAccess[];
    error?: string;
  }> {
    try {
      const url = `${this.getApiBase()}/users/${userId}/panel-access`;
      const response = await this.fetchWithAuth(url);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      this.handleError(error);
    }
  }

  /**
   * Met à jour l'accès à une section du panel pour un utilisateur
   */
  async updateUserPanelAccess(
    userId: number,
    section: string,
    hasAccess: boolean,
    reason?: string
  ): Promise<{
    success: boolean;
    message: string;
    data: PanelAccess;
    error?: string;
  }> {
    try {
      const url = `${this.getApiBase()}/users/${userId}/panel-access/${section}`;
      const response = await this.fetchWithAuth(url, {
        method: 'PATCH',
        body: JSON.stringify({ has_access: hasAccess, reason }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      this.handleError(error);
    }
  }

  /**
   * Récupère la liste des sections du panel disponibles
   */
  async getPanelSections(): Promise<{
    success: boolean;
    data: PanelSection[];
    error?: string;
  }> {
    try {
      const url = `${this.getApiBase()}/users/panel/sections`;
      const response = await this.fetchWithAuth(url);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      this.handleError(error);
    }
  }

  /**
   * Récupère l'historique des adresses IP d'un utilisateur
   */
  async getUserIpHistory(userId: number): Promise<{
    success: boolean;
    data: UserIpHistory[];
    error?: string;
  }> {
    try {
      const url = `${this.getApiBase()}/users/${userId}/ip-history`;
      const response = await this.fetchWithAuth(url);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      this.handleError(error);
    }
  }

  /**
   * Récupère tous les utilisateurs actuellement en ligne
   */
  async getOnlineUsers(): Promise<{
    success: boolean;
    data: Array<{ user_id: number; username: string; email: string; last_activity: string; session_count: number }>;
    error?: string;
  }> {
    try {
      const url = `${this.getApiBase()}/sessions/online`;
      const response = await this.fetchWithAuth(url);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      this.handleError(error);
    }
  }

  /**
   * Déconnecte de force un utilisateur (supprime toutes ses sessions)
   */
  async forceLogoutUser(userId: number): Promise<{
    success: boolean;
    message?: string;
    deletedCount?: number;
    error?: string;
  }> {
    try {
      const url = `${this.getApiBase()}/sessions/force-logout/${userId}`;
      const response = await this.fetchWithAuth(url, {
        method: 'POST',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      this.handleError(error);
    }
  }

  /**
   * Récupère les accès par défaut d'un rôle
   */
  async getRoleDefaultAccess(roleId: number): Promise<{
    success: boolean;
    data: RoleDefaultAccess[];
    error?: string;
  }> {
    try {
      const url = `${this.getApiBase()}/roles/${roleId}/default-access`;
      const response = await this.fetchWithAuth(url);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      this.handleError(error);
    }
  }

  /**
   * Récupère tous les accès par défaut de tous les rôles
   */
  async getAllRolesDefaultAccess(): Promise<{
    success: boolean;
    data: RoleDefaultAccess[];
    error?: string;
  }> {
    try {
      const url = `${this.getApiBase()}/roles/default-access`;
      const response = await this.fetchWithAuth(url);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      this.handleError(error);
    }
  }

  /**
   * Met à jour l'accès par défaut à une section du panel pour un rôle
   */
  async updateRoleDefaultAccess(
    roleId: number,
    section: string,
    hasAccess: boolean
  ): Promise<{
    success: boolean;
    message: string;
    data: RoleDefaultAccess;
    error?: string;
  }> {
    try {
      const url = `${this.getApiBase()}/roles/${roleId}/default-access/${section}`;
      const response = await this.fetchWithAuth(url, {
        method: 'PATCH',
        body: JSON.stringify({ has_access: hasAccess }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      this.handleError(error);
    }
  }
}
